import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

// Mirrors StandardGradingScale.cs in the API - the default every new school starts with.
const defaultScale = [
  { grade: 'A', range: '75 - 100', label: 'Excellent' },
  { grade: 'B', range: '65 - 74', label: 'Very good' },
  { grade: 'C', range: '50 - 64', label: 'Good (pass)' },
  { grade: 'D', range: '40 - 49', label: 'Satisfactory' },
  { grade: 'E', range: '30 - 39', label: 'Weak' },
  { grade: 'U', range: '0 - 29', label: 'Ungraded' },
];

/**
 * /features/zimsec-exams - targets "ZIMSEC school management software". States plainly
 * what Rongeka does NOT do (candidate registration, ZIMSEC results) so the page cannot
 * be read as an official ZIMSEC integration.
 */
export const ZimsecExamsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/zimsec-exams">
      <SEO
        title="ZIMSEC Grading, Exams and Results Software for Schools | Rongeka Africa"
        description="Run school tests and exams with ZIMSEC-style grading from A to U, publish results class by class, and send report cards to parents on WhatsApp."
        url="https://rongeka.africa/features/zimsec-exams"
        keywords="ZIMSEC school management software, ZIMSEC grading system, school exam management Zimbabwe, O Level results software, school results system Zimbabwe"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">EXAMS AND RESULTS</div>
        <h1 className="mkt-hero-title">ZIMSEC-Style Grading, Exams and Results</h1>
        <p className="mkt-hero-lead">
          Rongeka Africa runs your school's own tests and exams - from weekly tests to
          end-of-year exams - and grades them on the A to U scale Zimbabwean schools and
          parents already know.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">The default grading scale</h2>
          <p className="mkt-section-body">
            Every new school starts with this scale. The pass mark is 50%, and grades go
            down to U for Ungraded rather than F. Schools can change the bands to match
            their own policy.
          </p>
          <table className="mkt-grade-table">
            <caption className="sr-only">Default grading scale</caption>
            <thead>
              <tr>
                <th scope="col">Grade</th>
                <th scope="col">Mark (%)</th>
                <th scope="col">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {defaultScale.map((row) => (
                <tr key={row.grade}>
                  <th scope="row">{row.grade}</th>
                  <td>{row.range}</td>
                  <td>{row.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Every sitting kept separate</h2>
          <p className="mkt-section-body">
            Each exam is its own sitting within a term - a weekly test, a mid-term exam, the
            end-of-term exam or the end-of-year exam - and every subject paper belongs to
            one. A test in week one never gets mixed up with a test in week five, and each
            sitting appears as its own block on the report card.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Subjects tagged by curriculum: ZIMSEC O Level, ZIMSEC A Level, Cambridge, or your own</li>
            <li><CheckCircleOutlined />Teachers enter marks only for the subjects they teach</li>
            <li><CheckCircleOutlined />Grades and class positions worked out automatically</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Publishing results</h2>
          <p className="mkt-section-body">
            Results stay hidden from parents until the principal or school admin publishes
            them, a whole class at a time. Before publishing, Rongeka shows which subjects
            are still missing marks, so no parent opens a half-finished report card. If
            your school chooses to, results can be withheld from pupils whose fees are in
            arrears.
          </p>
          <p className="mkt-section-body">
            Published report cards reach parents on WhatsApp. See{' '}
            <RouterLink to="/features/reports">report cards</RouterLink> for what the card
            contains.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">What Rongeka does not do</h2>
          <p className="mkt-section-body">
            Rongeka manages your school's internal tests, exams and report cards. It is not
            connected to ZIMSEC: it does not register candidates or submit examination
            entries, and it does not receive official ZIMSEC results. "ZIMSEC-style" refers
            to the grading scale, not to an official link with the examinations council.
          </p>
          <p className="mkt-section-body">
            Running a secondary school? Read{' '}
            <RouterLink to="/secondary-schools">Rongeka for secondary schools</RouterLink>.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
};
